import type { ZodType } from 'zod';

import { ENVIRONMENTS, type Environment } from './environments.js';
import type { OperationImpact } from './impact.js';
import {
  assetsUploadImagesParams,
  bronzeGoldValidateParams,
  catalogExportParams,
  catalogImportParams,
  configExportParams,
  configImportParams,
  CONTAMINATION_DIMENSIONS,
  contaminationCrossOrgParams,
  csvIngestZipParams,
  dbBackupParams,
  dbRestoreParams,
  goldMaterializeProductRollupsParams,
  orgDeleteParams,
  orgExtractByBrandParams,
  orgExtractByBuParams,
  orgExtractPackageParams,
  orgExtractParams,
  orgInsertParams,
  orgWipeDataParams,
  productsDeleteByBrandParams,
  productsInsertWithSelloutParams,
  snapshotsLatestParams,
  sqlRunLibraryParams,
  userDeleteParams,
} from './params.js';
import type { ContaminationDimension } from './params.js';
import { RETAILER_SPLIT_PRESETS, RETAILER_SPLIT_PRESET_IDS } from './retailer-extract-scope.js';

/**
 * SSOT declarativa do catalogo de operacoes (spec/03-OPERATIONS-CATALOG.md).
 * Contrato universal: preflight -> impact -> plan -> execute.
 */

export type OperationId =
  | 'org.extract'
  | 'org.extract-by-brand'
  | 'org.extract-by-bu'
  | 'org.extract.package'
  | 'org.delete'
  | 'org.insert'
  | 'org.wipe-data'
  | 'bronze-gold.validate'
  | 'snapshots.latest'
  | 'gold.materialize-product-rollups'
  | 'products.delete-by-brand'
  | 'products.insert-with-sellout'
  | 'contamination.cross-org'
  | 'catalog.export'
  | 'catalog.import'
  | 'config.export'
  | 'config.import'
  | 'user.delete'
  | 'sql.run-library'
  | 'db.backup'
  | 'db.restore'
  | 'assets.upload-images'
  | 'csv.ingest-zip';

export type OperationGroup = 'organizacao' | 'dados' | 'catalogo' | 'usuarios' | 'sql' | 'banco';

/** Blocos de permissão da sidebar / RBAC (Consulta · Edição · Remoções). */
export const ACCESS_BLOCKS = ['consulta', 'edicao', 'remocao'] as const;
export type AccessBlock = (typeof ACCESS_BLOCKS)[number];

export const ACCESS_BLOCK_LABELS: Record<AccessBlock, string> = {
  consulta: 'Consulta',
  edicao: 'Edição',
  remocao: 'Remoções',
};

export const ACCESS_BLOCK_ORDER: readonly AccessBlock[] = ACCESS_BLOCKS;

/** Matriz capability × ambientes liberados para um operador. */
export type CapabilityGrants = Record<AccessBlock, Environment[]>;

export type FieldKind =
  | 'org-select'
  | 'brand-select'
  | 'category-select'
  | 'bu-select'
  | 'retailer-select'
  | 'retailer-multiselect'
  | 'user-select'
  | 'sql-library-select'
  | 'text'
  | 'textarea'
  | 'sql'
  | 'json'
  | 'number'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'file';

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormField {
  name: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  help?: string;
  options?: readonly FormFieldOption[];
  /** Campo que precisa estar preenchido antes deste (ex.: marca depende da org). */
  dependsOn?: string;
  /** Valor inicial na UI quando o formulário abre vazio. */
  defaultValue?: string | boolean;
  /** Esconde o campo até o irmão ter este valor (ex.: marca só no recorte `brand`). */
  visibleWhen?: { field: string; equals: string };
  /** `accept` do input file (ex.: `.zip,application/zip`). */
  accept?: string;
}

export interface OperationDef {
  id: OperationId;
  title: string;
  description: string;
  group: OperationGroup;
  /**
   * Bloco RBAC da sidebar. `sql.run-library` declara `consulta` no registry;
   * o executor sobe para `edicao` quando o SQL tiver writes.
   */
  accessBlock: AccessBlock;
  /** Rota da UI; `null` quando a operacao e disparada por outra tela (home/backups). */
  route: string | null;
  allowedEnvironments: readonly Environment[];
  paramsSchema: ZodType;
  fields: readonly FormField[];
  impact: OperationImpact;
}

const SCHEMA_OPTIONS: readonly FormFieldOption[] = [
  { value: 'public', label: 'public' },
  { value: 'datahouse', label: 'datahouse' },
  { value: 'providers_raw', label: 'providers_raw' },
  { value: 'gold', label: 'gold' },
  { value: 'bronze', label: 'bronze' },
  { value: 'silver', label: 'silver' },
];

const CONFIG_SECTION_OPTIONS: readonly FormFieldOption[] = [
  { value: 'settings', label: 'Settings da organização' },
  { value: 'dashboards', label: 'Dashboards habilitados' },
  { value: 'watched-sites', label: 'Sites monitorados' },
  { value: 'ads-targets', label: 'Metas de mídia paga' },
  { value: 'business-units', label: 'Business Units' },
  { value: 'connections-meta', label: 'Conexões (metadados, sem segredo)' },
];

const CATALOG_SECTION_OPTIONS: readonly FormFieldOption[] = [
  { value: 'brands', label: 'Marcas (brands)' },
  { value: 'categories', label: 'Categorias' },
  { value: 'products', label: 'Produtos (product_mappings)' },
  { value: 'links', label: 'Vínculos (product_mapping_links)' },
  { value: 'costs', label: 'Custos (product_costs)' },
  { value: 'site-rules', label: 'Regras por site (product_site_rules)' },
  { value: 'tags', label: 'Tags + product_base_tags' },
  { value: 'business-units', label: 'Business Units + members' },
];

const CONTAMINATION_DIMENSION_LABELS: Record<ContaminationDimension, string> = {
  keywords: 'Palavras-chave (SoV)',
  sovBrand: 'Marca no SoV',
  sovCategory: 'Categoria no SoV',
  eans: 'EANs (produtos)',
  catalogBrands: 'Cadastro de marcas (só leitura)',
  catalogCategories: 'Cadastro de categorias (só leitura)',
};

const ORG_FIELD: FormField = {
  name: 'organizationId',
  label: 'Organização',
  kind: 'org-select',
  required: true,
  help: 'Busque pelo nome; o id é enviado para o preflight.',
};

export const OPERATIONS: Readonly<Record<OperationId, OperationDef>> = {
  'org.extract': {
    id: 'org.extract',
    title: 'Extrair dados da organização',
    description:
      'Lê public + datahouse + providers_raw + gold + bronze + silver filtrados pela organização e gera artefato .sql (INSERT data-only) e .json. Opcionalmente filtra por varejistas (include/exclude).',
    group: 'organizacao',
    accessBlock: 'consulta',
    route: '/ops/org-extract',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: orgExtractParams,
    fields: [
      ORG_FIELD,
      {
        name: 'schemas',
        label: 'Schemas',
        kind: 'multiselect',
        options: SCHEMA_OPTIONS,
        help: 'Vazio = todos os schemas com coluna de tenant.',
      },
      { name: 'includeSnapshots', label: 'Incluir tabelas de snapshot', kind: 'boolean' },
      {
        name: 'retailerPreset',
        label: 'Preset de varejistas (Chamex/Sylvamo)',
        kind: 'select',
        options: RETAILER_SPLIT_PRESET_IDS.map((id) => ({
          value: id,
          label: RETAILER_SPLIT_PRESETS[id].label,
        })),
        help: 'Atalho opcional. Preenche mode + sites se siteConfigIds estiver vazio.',
      },
      {
        name: 'retailerMode',
        label: 'Modo do filtro de varejistas',
        kind: 'select',
        options: [
          { value: 'include', label: 'include — só os selecionados' },
          { value: 'exclude', label: 'exclude — org menos os selecionados' },
        ],
        help: 'Sem mode + sites = extract completo da org (comportamento legado).',
      },
      {
        name: 'siteConfigIds',
        label: 'Varejistas',
        kind: 'retailer-multiselect',
        dependsOn: 'organizationId',
        help: 'Sites monitorados da org (organization_watched_sites).',
      },
      {
        name: 'onConflict',
        label: 'Conflito no INSERT gerado',
        kind: 'select',
        options: [
          { value: 'do-nothing', label: 'ON CONFLICT DO NOTHING' },
          { value: 'do-update', label: 'ON CONFLICT DO UPDATE' },
        ],
      },
      { name: 'maxRowsPerTable', label: 'Máx. de linhas por tabela', kind: 'number' },
      { name: 'gzip', label: 'Gerar também .sql.gz', kind: 'boolean', defaultValue: true },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'Gera artefato .sql (INSERT data-only) e opcionalmente .json.',
        'Não altera o banco alvo — apenas SELECT/COPY OUT.',
        'Com filtro de varejistas, rollups org-wide (industry_product_d*) são omitidos — use gold.materialize-product-rollups após o insert.',
      ],
      requires: {},
    },
  },

  'org.extract-by-brand': {
    id: 'org.extract-by-brand',
    title: 'Extrair dados da org por marca ou categoria',
    description:
      'Mesma extração, escopada aos produtos da marca e/ou da categoria (brand_id/brand e category_id em public.product_mappings; gold/datahouse por product_base_id).',
    group: 'organizacao',
    accessBlock: 'consulta',
    route: '/ops/org-extract-by-brand',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: orgExtractByBrandParams,
    fields: [
      ORG_FIELD,
      {
        name: 'brandName',
        label: 'Marca',
        kind: 'brand-select',
        dependsOn: 'organizationId',
        help: 'Resolve por brands.id, name ou name_normalized. Informe marca e/ou categoria.',
      },
      {
        name: 'categoryName',
        label: 'Categoria',
        kind: 'category-select',
        dependsOn: 'organizationId',
        help: 'Resolve por categories.id, name ou name_normalized (inclui subárvore ltree quando existir).',
      },
      {
        name: 'onConflict',
        label: 'Conflito no INSERT gerado',
        kind: 'select',
        options: [
          { value: 'do-nothing', label: 'ON CONFLICT DO NOTHING' },
          { value: 'do-update', label: 'ON CONFLICT DO UPDATE' },
        ],
      },
      { name: 'maxRowsPerTable', label: 'Máx. de linhas por tabela', kind: 'number' },
      { name: 'gzip', label: 'Gerar também .sql.gz', kind: 'boolean', defaultValue: true },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'Extrai product_mappings da marca e/ou categoria, vínculos e fatias gold/datahouse resolvidas por product_base_id / ean.',
        'Não altera o banco alvo.',
      ],
      requires: {},
    },
  },

  'org.extract-by-bu': {
    id: 'org.extract-by-bu',
    title: 'Extrair dados da org por Business Unit',
    description:
      'Extrai public + gold + datahouse + bronze relevantes recortados pela membership materializada da BU (business_unit_products + business_unit_sites). Suba o artefato em /ops/org-insert numa org destino.',
    group: 'organizacao',
    accessBlock: 'consulta',
    route: '/ops/org-extract-by-bu',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: orgExtractByBuParams,
    fields: [
      ORG_FIELD,
      {
        name: 'businessUnitId',
        label: 'Business Unit',
        kind: 'bu-select',
        required: true,
        dependsOn: 'organizationId',
        help: 'Usa a membership já materializada (business_unit_products / business_unit_sites). Se a BU estiver stale, reconcilie antes no dashboard.',
      },
      {
        name: 'schemas',
        label: 'Schemas',
        kind: 'multiselect',
        options: SCHEMA_OPTIONS,
        help: 'Vazio = todos os schemas com coluna de tenant.',
      },
      { name: 'includeSnapshots', label: 'Incluir tabelas de snapshot', kind: 'boolean' },
      {
        name: 'onConflict',
        label: 'Conflito no INSERT gerado',
        kind: 'select',
        options: [
          { value: 'do-nothing', label: 'ON CONFLICT DO NOTHING' },
          { value: 'do-update', label: 'ON CONFLICT DO UPDATE' },
        ],
      },
      { name: 'maxRowsPerTable', label: 'Máx. de linhas por tabela', kind: 'number' },
      { name: 'gzip', label: 'Gerar também .sql.gz', kind: 'boolean', defaultValue: true },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'Extrai a BU + members + materialização, catálogo dos produtos da BU e fatias gold/datahouse/bronze por product_base_id (e site quando a BU restringe varejistas).',
        'industry_site_*, industry_summary_*, industry_sov_*, competitor_brands e csv_concorrente_* entram no artefato. industry_category_* omitido.',
        'Não altera o banco alvo — apenas SELECT.',
        'Após o insert na org destino, rematerialize industry_summary_* a partir de industry_product_* se o summary da fonte for org-wide demais (opcional).',
      ],
      requires: {},
    },
  },

  'org.extract.package': {
    id: 'org.extract.package',
    title: 'Empacotar extract para STG/PRD',
    description:
      'Valida um artefato .sql filtrado por varejista, reescreve organization_id/remaps de coexistência, cabeçalho com org destino e gera .sql + .manifest.json (+ .sql.gz opcional) para org.insert.',
    group: 'organizacao',
    accessBlock: 'consulta',
    route: '/ops/org-extract-package',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: orgExtractPackageParams,
    fields: [
      {
        name: 'artifactId',
        label: 'Artifact ID do extract',
        kind: 'text',
        required: true,
        help: 'ID do .sql gerado por org.extract (com filtro de varejistas).',
      },
      {
        name: 'destOrgId',
        label: 'UUID da org destino',
        kind: 'text',
        required: true,
        help: 'Ex.: Sylvamos Latam STG = 7e0e2931-d83b-4e1f-8269-489b6f58bc58',
      },
      {
        name: 'destOrgName',
        label: 'Nome da org destino',
        kind: 'text',
        required: true,
      },
      {
        name: 'splitLabel',
        label: 'Split',
        kind: 'select',
        options: [
          { value: 'latam', label: 'latam (validar 3 sites LATAM, 0 BR)' },
          { value: 'br', label: 'br (validar ausência LATAM)' },
        ],
      },
      {
        name: 'envLabel',
        label: 'Rótulo do ambiente',
        kind: 'text',
        help: 'Default: STG',
      },
      { name: 'gzip', label: 'Gerar também .sql.gz', kind: 'boolean' },
      {
        name: 'coexistenceRemap',
        label: 'Remapear IDs e corrigir GCS (fonte viva no mesmo banco)',
        kind: 'boolean',
        help: 'Default ligado. Reescreve organization_id no body, novos UUIDs para product_mappings/categories e restaura product-images/{origem}/.',
      },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'Não altera o banco alvo — só lê o artefato e grava novos arquivos no storage do toolkit.',
        'Validação falha se o split não bater (ex.: LATAM sem os 3 sites ou com refs BR).',
        'coexistenceRemap (default) reescreve organization_id no body, remapeia PKs e corrige GCS product-images/.',
      ],
      requires: {},
    },
  },

  'org.delete': {
    id: 'org.delete',
    title: 'Deletar organização (hard-delete)',
    description:
      'Limpa os schemas sem FK explicitamente e só então executa DELETE FROM organizations (cascade em ~60 tabelas public).',
    group: 'organizacao',
    accessBlock: 'remocao',
    route: '/ops/org-delete',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: orgDeleteParams,
    fields: [
      ORG_FIELD,
      {
        name: 'deleteOrphanedUsers',
        label: 'Também apagar usuários que ficarem órfãos',
        kind: 'boolean',
        help: 'Por padrão os usuários permanecem com organization_id = NULL.',
      },
    ],
    impact: {
      severity: 'irreversible',
      reversibility: 'só via backup',
      writes: [
        { schema: 'datahouse', table: '*', keyColumn: 'tenant_id', action: 'delete' },
        { schema: 'gold', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'providers_raw', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'bronze', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'silver', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'organizations', keyColumn: 'id', action: 'delete' },
      ],
      consequences: [
        'Remove o cliente por completo do banco alvo.',
        'Schemas sem FK NÃO cascateiam — o toolkit limpa explicitamente antes do DELETE da organização.',
        'Usuários não são apagados por padrão: users.organization_id vira NULL e as memberships caem no cascade.',
        'site_configs.owner_organization_id vira NULL — os sites sobrevivem.',
        'Dashboards e extensão dessa organização param de funcionar imediatamente.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'org.insert': {
    id: 'org.insert',
    title: 'Inserir dados na organização',
    description:
      'Aplica um artefato .sql de extração em uma organização destino, dentro de uma transação, com reescrita do organization_id.',
    group: 'organizacao',
    accessBlock: 'edicao',
    route: '/ops/org-insert',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: orgInsertParams,
    fields: [
      { ...ORG_FIELD, label: 'Organização destino' },
      {
        name: 'sql',
        label: 'SQL do artefato',
        kind: 'sql',
        help: 'Cole SQL pequeno, ou carregue o .sql/.sql.gz de org.extract / org.extract-by-brand / org.extract-by-bu — arquivos grandes sobem como artifact (sem pintar o textarea).',
      },
      { name: 'artifactId', label: 'Artifact ID (alternativa ao SQL colado)', kind: 'text' },
      {
        name: 'mode',
        label: 'Modo',
        kind: 'select',
        options: [
          { value: 'insert-only', label: 'insert-only (falha em conflito)' },
          { value: 'upsert', label: 'upsert (sobrescreve)' },
        ],
      },
      {
        name: 'materializeProductRollups',
        label: 'Materializar gold.industry_product_* após insert',
        kind: 'boolean',
        help: 'Recomendado após extract filtrado por varejista (rollups org-wide vêm omitidos).',
      },
      {
        name: 'coexistenceRemap',
        label: 'Remapear IDs e corrigir GCS (fonte viva no mesmo banco)',
        kind: 'boolean',
        help: 'Default ligado. Novos UUIDs para product_mappings e categories (evita ON CONFLICT / check_category_parent_org com a org de origem) e restaura product-images/{origem}/ no GCS.',
      },
      {
        name: 'localSchemaAdaptations',
        label: 'Adaptações de schema local',
        kind: 'boolean',
        help: 'jsonb→text[] em aliases já é automático no insert. Esta opção também omite tabelas irrelevantes (users, connections, …) e mantém categories.',
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'rollback na transação',
      writes: [{ schema: '*', table: '*', keyColumn: 'organization_id', action: 'insert' }],
      consequences: [
        'Escreve linhas no banco alvo na organização destino.',
        'insert-only falha em chave duplicada; upsert sobrescreve o que já existe.',
        'coexistenceRemap (default) remapeia PKs de product_mappings/categories e corrige URLs GCS product-images/.',
        'Não dispara o engine — gold/bronze podem ficar inconsistentes até o reprocessamento (ou materializeProductRollups).',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'org.wipe-data': {
    id: 'org.wipe-data',
    title: 'Wipe de dados da organização',
    description:
      'Zera dados analíticos e catálogo da organização mantendo o registro em organizations, usuários e login (runbook wipe-organization-data).',
    group: 'organizacao',
    accessBlock: 'remocao',
    route: '/ops/org-wipe-data',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: orgWipeDataParams,
    fields: [
      ORG_FIELD,
      {
        name: 'keepConnections',
        label: 'Manter conexões/credenciais de provider',
        kind: 'boolean',
        help: 'Desmarcado, apaga também public.connections da organização.',
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'só via backup',
      writes: [
        { schema: 'datahouse', table: '*', keyColumn: 'tenant_id', action: 'delete' },
        { schema: 'gold', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'providers_raw', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'bronze', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'silver', table: '*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'product_mapping_links', keyColumn: 'product_mapping_id', action: 'delete' },
        { schema: 'public', table: 'product_mappings', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'categories', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'brands', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'retailer_aliases', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'csv_uploads', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'organization_watched_sites', keyColumn: 'organization_id', action: 'delete' },
      ],
      consequences: [
        'A organização permanece (1 linha em organizations). Usuários e login permanecem.',
        'Os dashboards da organização ficam vazios até uma nova ingestão.',
        'Indicado antes de reingerir CSV/planilha do cliente.',
        'Schemas sem FK são limpos explicitamente — o Postgres não desfaz nada sozinho ali.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'bronze-gold.validate': {
    id: 'bronze-gold.validate',
    title: 'Validar sincronismo Bronze × Gold',
    description:
      'Compara sinais de bronze/raw contra gold por snapshot_date e reporta divergências (ok / warn / fail) por check.',
    group: 'dados',
    accessBlock: 'consulta',
    route: '/ops/bronze-gold-validate',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: bronzeGoldValidateParams,
    fields: [
      ORG_FIELD,
      { name: 'fromDate', label: 'De (snapshot_date)', kind: 'date' },
      { name: 'toDate', label: 'Até (snapshot_date)', kind: 'date' },
      {
        name: 'checks',
        label: 'Checks',
        kind: 'multiselect',
        options: [
          { value: 'industry-product-site', label: 'Industry produto × site' },
          { value: 'industry-site', label: 'Industry varejo' },
          { value: 'sov', label: 'Share of Voice' },
          { value: 'ratings-gap', label: 'Gap de ratings/reviews' },
          { value: 'quarantine', label: 'Quarentena de métricas de site' },
          { value: 'contract-quarantine', label: 'Quarentena de contrato' },
          { value: 'ads', label: 'Mídia paga (ads)' },
        ],
        help: 'Vazio = roda todos os checks disponíveis.',
      },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: ['Apenas reporta divergências (ok/warn/fail) por check e por snapshot_date.'],
      requires: {},
    },
  },

  'snapshots.latest': {
    id: 'snapshots.latest',
    title: 'Últimos snapshots',
    description:
      'Lista max(snapshot_date) e contagem por tabela/família para a organização, em todos os schemas com coluna de tenant.',
    group: 'dados',
    accessBlock: 'consulta',
    route: '/ops/snapshots-latest',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: snapshotsLatestParams,
    fields: [
      ORG_FIELD,
      {
        name: 'schemas',
        label: 'Schemas',
        kind: 'multiselect',
        options: SCHEMA_OPTIONS,
        help: 'Vazio = todos.',
      },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: ['Somente leitura de metadados de data por tabela.'],
      requires: {},
    },
  },

  'gold.materialize-product-rollups': {
    id: 'gold.materialize-product-rollups',
    title: 'Materializar rollups de produto (gold)',
    description:
      'Reconstrói gold.industry_product_d0/d7/d30/d90 a partir de industry_product_site_* (necessário após extract filtrado por varejista). Inclui image_url do catálogo.',
    group: 'dados',
    accessBlock: 'edicao',
    route: '/ops/gold-materialize-product-rollups',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: goldMaterializeProductRollupsParams,
    fields: [
      ORG_FIELD,
      {
        name: 'windows',
        label: 'Janelas',
        kind: 'multiselect',
        options: [
          { value: 'd0', label: 'd0' },
          { value: 'd7', label: 'd7' },
          { value: 'd30', label: 'd30' },
          { value: 'd90', label: 'd90' },
        ],
        help: 'Vazio = todas (d0, d7, d30, d90).',
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'só via backup',
      writes: [
        { schema: 'gold', table: 'industry_product_d0', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'gold', table: 'industry_product_d7', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'gold', table: 'industry_product_d30', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'gold', table: 'industry_product_d90', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'gold', table: 'industry_product_*', keyColumn: 'organization_id', action: 'insert' },
      ],
      consequences: [
        'Apaga e recria as linhas de industry_product_* da org a partir do grão site.',
        'image_url vem de product_mappings (URL GCS) — não de arquivo local.',
        'Portfólio / products-overview passam a listar SKUs após este passo.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'products.delete-by-brand': {
    id: 'products.delete-by-brand',
    title: 'Remover por marca ou varejista',
    description:
      'Remove o recorte de uma marca (catálogo + fatos) ou de um varejista (fatos de site, unwatch e, se marcado, os product_mappings observados nesse site) na organização, em public, bronze e gold.',
    group: 'catalogo',
    accessBlock: 'remocao',
    route: '/ops/products-delete-by-brand',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: productsDeleteByBrandParams,
    fields: [
      ORG_FIELD,
      {
        name: 'scope',
        label: 'Recorte',
        kind: 'select',
        required: true,
        defaultValue: 'brand',
        options: [
          { value: 'brand', label: 'Por marca' },
          { value: 'retailer', label: 'Por varejista' },
        ],
        help: 'Escolha exatamente um recorte. Marca apaga o catálogo desses SKUs; varejista limpa o site e, se marcado, apaga do catálogo da org os SKUs observados nele.',
      },
      {
        name: 'brandName',
        label: 'Marca',
        kind: 'brand-select',
        required: true,
        dependsOn: 'organizationId',
        visibleWhen: { field: 'scope', equals: 'brand' },
      },
      {
        name: 'siteConfigId',
        label: 'Varejista',
        kind: 'retailer-select',
        required: true,
        dependsOn: 'organizationId',
        visibleWhen: { field: 'scope', equals: 'retailer' },
        help: 'Watched sites da org e, se houver, sites que só restam em gold.industry_site (o Dashboard ainda lista esses). site_configs compartilhado não é apagado.',
      },
      {
        name: 'alsoWipeAnalytics',
        label: 'Também limpar bronze / gold / datahouse',
        kind: 'boolean',
        defaultValue: true,
      },
      {
        name: 'alsoUnwatchRetailer',
        label: 'Desvincular o varejista da organização',
        kind: 'boolean',
        defaultValue: true,
        visibleWhen: { field: 'scope', equals: 'retailer' },
        help: 'Remove organization_watched_sites e aliases da org. Não apaga site_configs (catálogo compartilhado).',
      },
      {
        name: 'alsoDeleteProducts',
        label: 'Apagar do catálogo os produtos observados neste varejista',
        kind: 'boolean',
        defaultValue: false,
        visibleWhen: { field: 'scope', equals: 'retailer' },
        help: 'Opt-in. Hard-delete de product_mappings (e fatos org-wide) dos SKUs que aparecem neste site — o SKU sai do catálogo da org inteira, mesmo que exista em outros varejistas. site_configs é compartilhado entre orgs; só fatos da org selecionada são tocados.',
      },
      {
        name: 'deleteEmptyBrand',
        label: 'Apagar a marca se ficar sem produtos',
        kind: 'boolean',
        visibleWhen: { field: 'scope', equals: 'brand' },
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'só via backup',
      writes: [
        { schema: 'public', table: 'product_mapping_links', keyColumn: 'product_mapping_id', action: 'delete' },
        { schema: 'public', table: 'product_mappings', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'bronze', table: 'csv_*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'gold', table: 'industry_product*', keyColumn: 'product_base_id', action: 'delete' },
        { schema: 'datahouse', table: 'product_*', keyColumn: 'product_base_id', action: 'delete' },
      ],
      consequences: [
        'Por marca: remove o catálogo desses SKUs na organização e, se marcado, os fatos bronze/gold/datahouse.',
        'Por varejista: remove fatos daquele site; se marcado, desvincula o watched site; se alsoDeleteProducts, apaga do catálogo da org os SKUs observados neste site (e seus fatos org-wide).',
        'site_configs permanece (catálogo compartilhado entre orgs).',
        'Sem limpar analytics, as fatias bronze/gold ficam órfãs.',
        'A linha da marca pode permanecer vazia (use a opção de apagá-la se ficar sem produtos).',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'contamination.cross-org': {
    id: 'contamination.cross-org',
    title: 'Limpar ingestão na org errada',
    description:
      'Cruza o universo de uma org de referência (palavras-chave, marcas e categorias do SoV, EANs, cadastro de marcas/categorias) com a org a limpar. O preflight só detecta; o execute apaga apenas o overlap no SoV (bronze.csv_sov_dia + gold.industry_sov_*) e, se marcado, os produtos com EAN da referência.',
    group: 'dados',
    accessBlock: 'remocao',
    route: '/ops/contamination-cross-org',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: contaminationCrossOrgParams,
    fields: [
      {
        ...ORG_FIELD,
        label: 'Organização a limpar',
        help: 'Org que recebeu a ingestão errada. O token de confirmação é o nome exato desta org.',
      },
      {
        name: 'sourceOrganizationId',
        label: 'Organização de referência (dona dos dados)',
        kind: 'org-select',
        required: true,
        help: 'Org de onde os dados vieram. Só é lida — nunca alterada.',
      },
      {
        name: 'dimensions',
        label: 'Dimensões',
        kind: 'multiselect',
        options: CONTAMINATION_DIMENSIONS.map((value) => ({
          value,
          label: CONTAMINATION_DIMENSION_LABELS[value],
        })),
        help: 'Vazio = todas. Palavras-chave habilita o DELETE de SoV; marca/categoria do SoV estreitam esse DELETE; cadastro de marcas/categorias é só leitura.',
      },
      {
        name: 'alsoDeleteProducts',
        label: 'Apagar produtos cujo EAN pertence à org de referência',
        kind: 'boolean',
        defaultValue: false,
        help: 'Opt-in. Hard-delete de product_mappings (e fatos bronze/gold/datahouse por EAN / product_base_id) na org a limpar. Exige a dimensão EAN.',
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'só via backup',
      writes: [
        { schema: 'bronze', table: 'csv_sov_dia', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'gold', table: 'industry_sov_*', keyColumn: 'organization_id', action: 'delete' },
        { schema: 'public', table: 'product_mapping_links', keyColumn: 'product_mapping_id', action: 'delete' },
        { schema: 'public', table: 'product_mappings', keyColumn: 'organization_id', action: 'delete' },
      ],
      consequences: [
        'Só o overlap com a org de referência é apagado — SoV e produtos da org a limpar que não existem na referência permanecem.',
        'Cadastro de marcas e categorias é apenas reportado (nomes iguais entre orgs costumam ser falso positivo).',
        'A org de referência nunca é alterada.',
        'Depois do execute, reenvie o fato-sov-dia correto da org limpa; industry_site_*.market_share e datahouse.search_term_kpi_* não são recalculados aqui.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'products.insert-with-sellout': {
    id: 'products.insert-with-sellout',
    title: 'Inserir produtos e sellout',
    description:
      'Insere produtos em public.product_mappings (criando brands/categories quando necessário) e fatos diários no landing bronze.',
    group: 'catalogo',
    accessBlock: 'edicao',
    route: '/ops/products-insert-sellout',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: productsInsertWithSelloutParams,
    fields: [
      ORG_FIELD,
      {
        name: 'products',
        label: 'Produtos (JSON)',
        kind: 'json',
        required: true,
        help: '[{ "ean": "789...", "name": "Produto", "brand": "Marca", "category": "Categoria" }]',
      },
      {
        name: 'sellout',
        label: 'Sellout diário (JSON)',
        kind: 'json',
        help: '[{ "ean": "789...", "snapshotDate": "2026-08-10", "varejo": "Varejo X", "selloutValor": 1234.5, "selloutUnidades": 10 }]',
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'rollback na transação',
      writes: [
        { schema: 'public', table: 'brands', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'product_mappings', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'bronze', table: 'csv_produto_site_dia', keyColumn: 'organization_id', action: 'insert' },
      ],
      consequences: [
        'Os dados entram no landing bronze e no catálogo public; o gold exige reprocessamento do engine.',
        'Números seguem a leitura canônica de CSV do monorepo (última "." ou "," é o decimal).',
        'Em caso de erro, a transação inteira sofre rollback.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'catalog.export': {
    id: 'catalog.export',
    title: 'Exportar catálogo canônico',
    description:
      'Extrai o master de produtos da organização (brands, categories, product_mappings e satélites) para .sql e .json.',
    group: 'catalogo',
    accessBlock: 'consulta',
    route: '/ops/catalog-export',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: catalogExportParams,
    fields: [
      ORG_FIELD,
      {
        name: 'include',
        label: 'Seções',
        kind: 'multiselect',
        options: CATALOG_SECTION_OPTIONS,
        help: 'Vazio = todas as seções do catálogo canônico.',
      },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'Somente leitura.',
        'Não inclui fatos bronze/gold/datahouse nem site_configs compartilhados.',
        'product_mapping_links sai via JOIN com product_mappings da org.',
      ],
      requires: {},
    },
  },

  'catalog.import': {
    id: 'catalog.import',
    title: 'Re-ingerir catálogo canônico',
    description:
      'Reaplica um artefato de catálogo canônico na mesma organização (merge ou replace-selected). Cross-org é recusado.',
    group: 'catalogo',
    accessBlock: 'edicao',
    route: '/ops/catalog-import',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: catalogImportParams,
    fields: [
      { ...ORG_FIELD, label: 'Organização (mesma do artefato)' },
      { name: 'sql', label: 'SQL do artefato', kind: 'sql' },
      { name: 'artifactId', label: 'Artifact ID (alternativa ao SQL colado)', kind: 'text' },
      {
        name: 'mode',
        label: 'Modo',
        kind: 'select',
        options: [
          { value: 'merge', label: 'merge (upsert)' },
          { value: 'replace-selected', label: 'replace-selected (apaga a seção antes)' },
        ],
      },
      { name: 'include', label: 'Seções', kind: 'multiselect', options: CATALOG_SECTION_OPTIONS },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'só via backup',
      writes: [
        { schema: 'public', table: 'brands', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'categories', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'product_mappings', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'product_mapping_links', keyColumn: 'product_mapping_id', action: 'insert' },
        { schema: 'public', table: 'product_costs', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'product_site_rules', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'tags', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'product_base_tags', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'business_units', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'business_unit_members', keyColumn: 'organization_id', action: 'insert' },
      ],
      consequences: [
        'Só na mesma organização do cabeçalho do artefato — clone cross-org é recusado.',
        'No modo replace-selected as linhas atuais das seções escolhidas são apagadas antes da carga.',
        'Não remapeia UUIDs; PKs do artefato precisam casar com as constraints do destino.',
        'business_unit_products (materialização) não entra — reconcilie a BU depois se necessário.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'assets.upload-images': {
    id: 'assets.upload-images',
    title: 'Upload de imagens (ZIP)',
    description:
      'Sobe um ZIP de pastas EAN para o bucket público fds-refs/{org}/ e devolve CSV ean,imagem_url para o dim-produto. Não grava product_mappings.',
    group: 'dados',
    accessBlock: 'edicao',
    route: '/ops/assets-upload-images',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: assetsUploadImagesParams,
    fields: [
      ORG_FIELD,
      {
        name: 'artifactId',
        label: 'ZIP de imagens',
        kind: 'file',
        required: true,
        accept: '.zip,application/zip',
        help: 'Pastas com EAN no nome; arquivos png/jpg/jpeg/webp/gif viram {ean}-{id}.ext no bucket público.',
      },
      {
        name: 'prefix',
        label: 'Prefixo no bucket',
        kind: 'text',
        defaultValue: 'fds-refs',
        help: 'Padrão fds-refs. O objeto fica {prefix}/{organizationId}/{ean}-{id}.ext.',
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'irreversível',
      writes: [
        { schema: 'gcs', table: 'public-assets', keyColumn: 'organization_id', action: 'insert' },
      ],
      consequences: [
        'Grava objetos no bucket público (cache de 1 ano, leitura anônima). Não envia CSV para este bucket.',
        'Não altera product_mappings — cole o CSV ean,imagem_url no dim-produto e ingira pela operação de ZIP CSV.',
        'Em local o toolkit grava em disco (ARTIFACT_DIR/public-assets), sem escrever no bucket de produção.',
      ],
      requires: { typedToken: 'environment' },
    },
  },

  'csv.ingest-zip': {
    id: 'csv.ingest-zip',
    title: 'Ingerir ZIP de CSV',
    description:
      'Envia os CSVs do contrato v2 (um a um, na ordem) via API Visualitics para o data-lake privado. O ZIP nunca vai ao bucket público.',
    group: 'dados',
    accessBlock: 'edicao',
    route: '/ops/csv-ingest-zip',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: csvIngestZipParams,
    fields: [
      ORG_FIELD,
      {
        name: 'artifactId',
        label: 'ZIP de CSV',
        kind: 'file',
        required: true,
        accept: '.zip,application/zip',
        help: 'Arquivos .csv nomeados pelos slugs do contrato (dim-produto.csv, fato-produto-dia.csv, …).',
      },
      {
        name: 'skipInvalid',
        label: 'Descartar linhas inválidas',
        kind: 'boolean',
        defaultValue: false,
      },
      {
        name: 'skipMismatchedRetailers',
        label: 'Descartar varejos não registrados',
        kind: 'boolean',
        defaultValue: false,
      },
      {
        name: 'processAfterUpload',
        label: 'Processar no engine após cada upload',
        kind: 'boolean',
        defaultValue: true,
        help: 'O próximo CSV só sobe depois do anterior completed — fato-produto-dia registra varejo.',
      },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'só via backup',
      writes: [
        { schema: 'public', table: 'csv_uploads', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'bronze', table: 'csv_*', keyColumn: 'organization_id', action: 'insert' },
      ],
      consequences: [
        'O CSV vai para o data-lake privado da Visualitics ({org}/{slug}/…), nunca para o bucket público de imagens.',
        'O trabalho real não é SQL: stream + process via API. O EXPLAIN do plano só valida a organização no Postgres.',
        'fato-produto-dia registra varejistas; os templates seguintes dependem disso — a ordem do contrato é obrigatória.',
        'Credencial Visualitics do profile nunca é gravada em ops_runs.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'environment' },
    },
  },

  'config.export': {
    id: 'config.export',
    title: 'Exportar configurações da organização',
    description:
      'Exporta settings, dashboards habilitados, sites monitorados, metas de ads e business units para .sql e .json.',
    group: 'organizacao',
    accessBlock: 'consulta',
    route: '/ops/config-export',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: configExportParams,
    fields: [
      ORG_FIELD,
      {
        name: 'include',
        label: 'Seções',
        kind: 'multiselect',
        options: CONFIG_SECTION_OPTIONS,
        help: 'Vazio = todas as seções disponíveis.',
      },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'Somente leitura.',
        'Segredos de provider não são exportados em claro — o ciphertext é omitido.',
      ],
      requires: {},
    },
  },

  'config.import': {
    id: 'config.import',
    title: 'Importar configurações da organização',
    description: 'Aplica um artefato de configurações na organização destino, dentro de uma transação.',
    group: 'organizacao',
    accessBlock: 'edicao',
    route: '/ops/config-import',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: configImportParams,
    fields: [
      { ...ORG_FIELD, label: 'Organização destino' },
      { name: 'sql', label: 'SQL do artefato', kind: 'sql' },
      { name: 'artifactId', label: 'Artifact ID (alternativa ao SQL colado)', kind: 'text' },
      {
        name: 'mode',
        label: 'Modo',
        kind: 'select',
        options: [
          { value: 'merge', label: 'merge (upsert)' },
          { value: 'replace-selected', label: 'replace-selected (apaga a seção antes)' },
        ],
      },
      { name: 'include', label: 'Seções', kind: 'multiselect', options: CONFIG_SECTION_OPTIONS },
    ],
    impact: {
      severity: 'destructive',
      reversibility: 'só via backup',
      writes: [
        { schema: 'public', table: 'organization_settings', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'organization_dashboard_settings', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'ads_targets', keyColumn: 'organization_id', action: 'insert' },
        { schema: 'public', table: 'business_units', keyColumn: 'organization_id', action: 'insert' },
      ],
      consequences: [
        'Altera comportamento de dashboards, feature flags e metas da organização.',
        'Credenciais de provider não são importadas por este fluxo.',
        'No modo replace-selected as linhas atuais da seção são apagadas antes da carga.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'org-name' },
    },
  },

  'user.delete': {
    id: 'user.delete',
    title: 'Deletar usuário',
    description:
      'Reatribui as 4 FKs RESTRICT do usuário e então executa o DELETE, deixando as cascatas de login/refresh/memberships caírem.',
    group: 'usuarios',
    accessBlock: 'remocao',
    route: '/ops/user-delete',
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: userDeleteParams,
    fields: [
      { name: 'userId', label: 'Usuário', kind: 'user-select', required: true },
      {
        name: 'reassignToUserId',
        label: 'Reatribuir acervo para',
        kind: 'user-select',
        help: 'Obrigatório se o usuário tiver data_assets, folders, share tokens ou quota_requests.',
      },
    ],
    impact: {
      severity: 'irreversible',
      reversibility: 'só via backup',
      writes: [
        { schema: 'public', table: 'data_assets', keyColumn: 'created_by_user_id', action: 'update' },
        { schema: 'public', table: 'data_asset_folders', keyColumn: 'created_by_user_id', action: 'update' },
        { schema: 'public', table: 'data_asset_share_tokens', keyColumn: 'created_by_user_id', action: 'update' },
        { schema: 'public', table: 'quota_requests', keyColumn: 'requested_by_user_id', action: 'update' },
        { schema: 'public', table: 'users', keyColumn: 'id', action: 'delete' },
      ],
      consequences: [
        'Quatro FKs RESTRICT (data_assets, data_asset_folders, data_asset_share_tokens, quota_requests) precisam ser reatribuídas antes do delete.',
        'Cascata: login_methods, refresh_tokens, email_change_requests e user_organizations são apagados junto.',
        'As sessões ativas do usuário deixam de funcionar imediatamente.',
      ],
      requires: { backupWithinHours: 24, typedToken: 'user-email' },
    },
  },

  'sql.run-library': {
    id: 'sql.run-library',
    title: 'Executar SQL da biblioteca',
    description:
      'Roda um SQL salvo (ou colado) pelo mesmo pipeline de 4 fases. A severidade é derivada do parser: escrita vira destrutiva.',
    group: 'sql',
    accessBlock: 'consulta',
    route: '/ops/sql-library-run',
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: sqlRunLibraryParams,
    fields: [
      { name: 'sqlLibraryId', label: 'Item da biblioteca', kind: 'sql-library-select' },
      { name: 'sql', label: 'SQL', kind: 'sql', help: 'Deixe vazio para usar o SQL do item selecionado.' },
      { name: 'maxRows', label: 'Máx. de linhas no resultado', kind: 'number' },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'A severidade real é recalculada no preflight a partir do SQL: INSERT/UPDATE/DELETE/TRUNCATE/DROP/ALTER tornam a operação destrutiva.',
        'Em produção, somente SQL marcado como read-only é aceito.',
      ],
      requires: {},
    },
  },

  'db.backup': {
    id: 'db.backup',
    title: 'Backup do banco',
    description:
      'Gera backup do banco alvo (full via pg_dump -Fc, schema-only, ou por organização via COPY filtrado) no volume toolkit_backups.',
    group: 'banco',
    accessBlock: 'consulta',
    route: null,
    allowedEnvironments: ['local', 'staging', 'production'],
    paramsSchema: dbBackupParams,
    fields: [
      {
        name: 'mode',
        label: 'Modo',
        kind: 'select',
        required: true,
        options: [
          { value: 'full', label: 'Full (pg_dump -Fc)' },
          { value: 'schema-only', label: 'Somente schema' },
          { value: 'organization', label: 'Por organização (COPY filtrado)' },
        ],
      },
      { name: 'organizationId', label: 'Organização', kind: 'org-select' },
      { name: 'note', label: 'Nota', kind: 'text' },
    ],
    impact: {
      severity: 'read-only',
      reversibility: 'nenhum efeito',
      writes: [],
      consequences: [
        'Gera um arquivo no volume toolkit_backups; nada é escrito no banco alvo.',
        'Full e schema-only usam pg_dump; por organização usa COPY filtrado por organization_id/tenant_id.',
        'Em produção exige usuário com leitura ampla (pg_read_all_data).',
      ],
      requires: {},
    },
  },

  'db.restore': {
    id: 'db.restore',
    title: 'Restaurar backup',
    description:
      'Restaura um backup no banco alvo. Bloqueado em produção pela allowlist — lá o restore continua sendo runbook manual.',
    group: 'banco',
    accessBlock: 'edicao',
    route: null,
    allowedEnvironments: ['local', 'staging'],
    paramsSchema: dbRestoreParams,
    fields: [
      { name: 'backupId', label: 'Backup', kind: 'text', required: true },
      { name: 'dropExisting', label: 'Limpar objetos existentes antes', kind: 'boolean' },
    ],
    impact: {
      severity: 'irreversible',
      reversibility: 'irreversível',
      writes: [{ schema: '*', table: '*', keyColumn: 'id', action: 'update' }],
      consequences: [
        'Sobrescreve o conteúdo do banco alvo com o do arquivo escolhido.',
        'Restore em produção é bloqueado pela allowlist (C10 da constitution).',
      ],
      requires: { typedToken: 'environment' },
    },
  },
};

export const OPERATION_IDS = Object.keys(OPERATIONS) as OperationId[];

/** Catálogo da UI (db.restore é disparado pela tela de backups, fora desta lista). */
export const CATALOG_OPERATION_IDS: readonly OperationId[] = OPERATION_IDS.filter(
  (id) => id !== 'db.restore',
);

export function isOperationId(value: unknown): value is OperationId {
  return typeof value === 'string' && value in OPERATIONS;
}

export function getOperation(id: OperationId): OperationDef {
  return OPERATIONS[id];
}

export const OPERATION_GROUP_LABELS: Record<OperationGroup, string> = {
  organizacao: 'Organização',
  dados: 'Dados',
  catalogo: 'Catálogo',
  usuarios: 'Usuários',
  sql: 'SQL',
  banco: 'Banco',
};

/** Allowlist default do registry; overrides por conexão vencem (spec §3 do catálogo). */
export function defaultAllows(id: OperationId, environment: Environment): boolean {
  return OPERATIONS[id].allowedEnvironments.includes(environment);
}

export function getAccessBlock(id: OperationId): AccessBlock {
  return OPERATIONS[id].accessBlock;
}

/** Ops com rota de UI agrupadas por bloco RBAC (ordem da sidebar). */
export function operationsByAccessBlock(): Record<AccessBlock, OperationDef[]> {
  const result: Record<AccessBlock, OperationDef[]> = {
    consulta: [],
    edicao: [],
    remocao: [],
  };
  for (const id of OPERATION_IDS) {
    const op = OPERATIONS[id];
    if (op.route === null) continue;
    result[op.accessBlock].push(op);
  }
  return result;
}

/** Grants vazios (nenhuma capability × ambiente). */
export function emptyCapabilityGrants(): CapabilityGrants {
  return { consulta: [], edicao: [], remocao: [] };
}

/** Grants completos (3 capabilities × todos os ambientes). */
export function fullCapabilityGrants(): CapabilityGrants {
  return {
    consulta: [...ENVIRONMENTS],
    edicao: [...ENVIRONMENTS],
    remocao: [...ENVIRONMENTS],
  };
}

export function hasCapabilityGrant(
  grants: CapabilityGrants,
  capability: AccessBlock,
  environment: Environment,
): boolean {
  return grants[capability].includes(environment);
}